<script lang="ts">
    /**
     * ProductsSection - 회사 프로덕트 카드 묶음
     *
     * JadEye(서비스 중) + Gridverse(서비스 중) + Pin(서비스 중) + Ink(서비스 중)을 ProductCard 로 소개.
     * 셋 다 프로덕트 상세 페이지(/jadeye, /gridverse, /pin)로 연결한다 — Pin 도 D9 이후로
     * 소개 페이지가 생겨 다른 둘과 같은 층이다(코드 :href={pin.landing} 가 정본, 이 주석이 예전에
     * "/pin/rest-area 로 바로" 라고 말해 코드와 정면으로 어긋나 있었다).
     */
    import ProductCard from '../../components/ProductCard.svelte';
    import { getPin } from '../../../../lib/pin';
    import { getInk } from '../../../../lib/ink';

    // 📍 꺼진 환경(dev)에서는 null → 카드도, 부제의 Pin 문구도 나오지 않는다.
    const pin = getPin();
    // 🖋️ Ink 도 같다 — 꺼지면 카드도 부제의 Ink 문구도 나오지 않는다.
    const ink = getInk();

    // 부제는 켜진 서비스의 조합마다 한 벌이다 — 서버의 Seo::servicesSubtitle()(noscript)과 **같은 네 문장**이다.
    const subtitle =
        pin && ink
            ? '건강과 모험, 길 위의 정보, 그리고 이야기를 만듭니다'
            : pin
              ? '건강과 모험, 그리고 길 위의 정보를 만듭니다'
              : ink
                ? '건강과 모험, 그리고 이야기를 만듭니다'
                : '건강과 모험, 두 개의 세계를 만듭니다';

    let isVisible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            isVisible = true;
        }, 150);
        return () => clearTimeout(t);
    });
</script>

<section id="products" class="products-section">
    <div class="products-inner" class:visible={isVisible}>
        <div class="products-head">
            <h2 class="products-title">우리의 서비스</h2>
            <!-- 부제를 통째로 분기한다 — "두 개의 세계"처럼 개수를 세는 문장은 카드 수와 어긋나면
                 바로 거짓말이 된다. 한 줄짜리라 두 벌을 나란히 두는 편이 덜 위험하다. -->
            <p class="products-subtitle">{subtitle}</p>
        </div>

        <div class="products-grid">
            <ProductCard
                name="JadEye"
                koName="제이드아이"
                tagline="함께하는 스마트 건강 관리"
                description="근처 친구와 자동으로 시작되는 협동 건강 챌린지. AI 건강 분석부터 게이미피케이션까지, 혼자가 아닌 함께하는 건강을 만듭니다."
                icon="/logo.svg"
                iconIsImage={true}
                accent="#1E5C3A"
                badge="서비스 중"
                href="/jadeye"
                ctaLabel="자세히 보기"
                features={['근거리 협동 챌린지', 'AI 건강 분석', '게이미피케이션 헬스케어']}
            />

            <ProductCard
                name="Gridverse"
                tagline="걸어서 개척하는 다차원 AR 모험"
                description="현실 공간을 걸으며 나만의 차원에서 기지를 짓고 영역을 넓혀가는 위치기반 AR 게임. 시즌의 끝엔 모든 차원이 만나는 대동굿이 기다려요. 지금 Google Play와 App Store에서 만나보세요."
                icon="🎮"
                accent="#B49B50"
                badge="서비스 중"
                href="/gridverse"
                ctaLabel="자세히 보기"
                features={['걸어서 영역 개척', '시즌제 나만의 차원', '정성이 모이는 대동굿']}
            />

            {#if pin}
                <!--
                    📍 Pin — 세 카드가 **전부 제품 소개 페이지**로 간다(D9). 처음엔 Pin 만 콘텐츠
                       (/pin/rest-area)로 직결했는데, 그러면 소개 없이 raw 목록으로 떨어져
                       JadEye·Gridverse 와 형태가 어긋났다. ctaLabel 도 다른 둘과 같은 문구로 맞춘다.

                    ⚠️ 개수("286곳")를 쓰지 않는다. 두 가지 이유:
                       ① 도로공사 데이터는 주 1회 배치로 갱신돼 하드코딩한 숫자는 조용히 낡는다.
                       ② 실시간 개수를 읽으려면 랜딩이 Pin 서비스(DB)를 질의해야 하는데,
                          그러면 회사 홈의 가용성이 격리 모듈에 묶인다 — 방향이 반대다.
                -->
                <ProductCard
                    name="Pin"
                    tagline="길 위에서 찾는 진짜 정보"
                    description="고속도로 휴게소의 메뉴·편의시설부터 오래된 식당, 전국 등산코스까지. 길 위에서 필요한 정보를 공공데이터로 모아 정리했어요."
                    icon="📍"
                    accent="#2E6F9E"
                    badge="서비스 중"
                    href={pin.landing}
                    ctaLabel="자세히 보기"
                    features={['고속도로 휴게소 메뉴·편의시설', '인허가 원장으로 찾은 오래된 식당', '전국 산 등산코스와 경로 지도']}
                />
            {/if}

            {#if ink}
                <!-- 🖋️ Ink — 다른 카드와 같이 제품 소개 페이지(대문)로 간다. 문구와 작품 이름은 서버 주입값이다. -->
                <ProductCard
                    name="Ink"
                    tagline={ink.tagline}
                    description={ink.subtitle.join(' ')}
                    icon="🖋️"
                    accent="#3F4A72"
                    badge="서비스 중"
                    href={ink.landing}
                    ctaLabel="자세히 보기"
                    features={ink.works.map((work) => work.label)}
                />
            {/if}
        </div>
    </div>
</section>

<style>
    .products-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg, #ffffff);
    }

    .products-inner {
        max-width: 72rem;
        margin: 0 auto;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .products-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .products-head {
        text-align: center;
        margin-bottom: 3rem;
    }

    .products-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.75rem;
    }

    .products-subtitle {
        font-size: 1.05rem;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    /*
     * 카드 수에 따라 열이 저절로 접히는 auto-fit 그리드.
     * 프로덕트가 2개 → 3개로 늘어난 참에, 열 수를 미디어쿼리로 세는 대신 폭으로 결정한다
     * (4번째가 와도 CSS 를 다시 안 건드린다). 하한 17rem 은 스크롤바(약 15px)를 뺀 실폭 기준:
     *   1024px − 좌우 3rem − 스크롤바 ≈ 913px  →  3열(272*3 + 32*2 = 880) 들어감
     *    768px − 좌우 2.5rem − 스크롤바 ≈ 673px →  3열(880) 불가 → 2열(576)
     *   ⚠️ 18rem 으로 잡으면 1024px 에서 필요폭이 928px 이 되어 스크롤바 때문에 2열로 떨어진다.
     *
     * 🚨 min(17rem, 100%) 인 이유: 컨테이너가 하한보다 좁아지면(초소형 단말) 트랙이 컨테이너를
     *    넘어서 **가로 스크롤**이 생긴다. 100% 로 눌러 그 상황을 구조적으로 없앤다.
     */
    .products-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(17rem, 100%), 1fr));
        gap: 1.5rem;
        max-width: 72rem;
        margin: 0 auto;
    }

    @media (min-width: 768px) {
        .products-section {
            padding: 6rem 2.5rem;
        }

        .products-grid {
            gap: 2rem;
        }
    }

    @media (min-width: 1024px) {
        .products-section {
            padding: 7rem 3rem;
        }
    }
</style>
